/**
 * Auth-provider registry and config for the frontend.
 *
 * Users pick which provider to sign in with at the login screen. Entra uses
 * MSAL (popup / chrome.identity); both B2C providers use a custom email /
 * password form that POSTs to their `oauth2/v2.0/token` endpoint (ROPC user
 * flow). The backend validates all three token shapes via its multi-issuer
 * validator.
 *
 * Provider config is driven by env vars so a deployment can enable a subset
 * (e.g. prod might expose only Entra; dev exposes all three).
 */

import { featureFlags } from "../config/featureFlags";

export type AuthProviderId = "entra" | "pathways-b2c" | "provider-portal-b2c";

export interface EntraConfig {
  id: "entra";
  label: string;
  clientId: string;
  tenantId: string;
  apiScopes: string[];   // e.g. ["api://<id>/.default"]. MSAL takes an array.
  redirectUri: string;
}

export interface B2cConfig {
  id: "pathways-b2c" | "provider-portal-b2c";
  label: string;
  domainName: string;    // e.g. "nmmccauth"
  userFlow: string;      // e.g. "b2c_1a_ropc_auth"
  clientId: string;      // B2C app registration client id (audience + scope)
}

export type AuthProviderConfig = EntraConfig | B2cConfig;

/**
 * Provider enablement is driven by login feature flags (see
 * src/config/featureFlags.ts). Each method has an explicit boolean flag with a
 * default: SSO on, Pathways and Provider Portal off. Per-provider config env
 * vars (below) must still be populated, otherwise the provider is dropped with
 * a console.warn even if its feature flag is on.
 *
 *   VITE_FEATURE_LOGIN_SSO              (default true)
 *   VITE_FEATURE_LOGIN_PATHWAYS         (default false)
 *   VITE_FEATURE_LOGIN_PROVIDER_PORTAL  (default false)
 *
 * Per-provider config (required when the corresponding flag is on):
 *   VITE_AZURE_CLIENT_ID / VITE_AZURE_TENANT_ID / VITE_CARE_MANAGEMENT_GQL_SCOPES / VITE_AZURE_REDIRECT_URI
 *   VITE_PATHWAYS_B2C_DOMAIN / VITE_PATHWAYS_B2C_POLICY / VITE_PATHWAYS_B2C_CLIENT_ID
 *   VITE_PROVIDER_PORTAL_B2C_DOMAIN / VITE_PROVIDER_PORTAL_B2C_POLICY / VITE_PROVIDER_PORTAL_B2C_CLIENT_ID
 */
export function parseEnabledIds(): AuthProviderId[] {
  const ids: AuthProviderId[] = [];
  if (featureFlags.loginSso) ids.push("entra");
  if (featureFlags.loginPathways) ids.push("pathways-b2c");
  if (featureFlags.loginProviderPortal) ids.push("provider-portal-b2c");
  return ids;
}

function entraConfig(): EntraConfig | null {
  const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
  const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
  const rawScopes = import.meta.env.VITE_CARE_MANAGEMENT_GQL_SCOPES;
  if (!clientId || !tenantId || !rawScopes) return null;
  const apiScopes = rawScopes.split(/\s+/).map((s) => s.trim()).filter(Boolean);
  if (apiScopes.length === 0) return null;
  return {
    id: "entra",
    label: "Astrana SSO",
    clientId,
    tenantId,
    apiScopes,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
  };
}

// Vite statically replaces `import.meta.env.VITE_FOO` (literal property
// access) at build time. Dynamic access — `import.meta.env[\`VITE_${x}\`]` —
// is not reliably replaced, so the values come back `undefined` in
// production bundles even when the env vars are populated. Keep these
// reads as static property accesses, one branch per provider.
function b2cConfig(
  id: "pathways-b2c" | "provider-portal-b2c",
  label: string,
): B2cConfig | null {
  const domainName =
    id === "pathways-b2c"
      ? import.meta.env.VITE_PATHWAYS_B2C_DOMAIN
      : import.meta.env.VITE_PROVIDER_PORTAL_B2C_DOMAIN;
  const userFlow =
    id === "pathways-b2c"
      ? import.meta.env.VITE_PATHWAYS_B2C_POLICY
      : import.meta.env.VITE_PROVIDER_PORTAL_B2C_POLICY;
  const clientId =
    id === "pathways-b2c"
      ? import.meta.env.VITE_PATHWAYS_B2C_CLIENT_ID
      : import.meta.env.VITE_PROVIDER_PORTAL_B2C_CLIENT_ID;
  if (!domainName || !userFlow || !clientId) return null;
  return { id, label, domainName, userFlow, clientId };
}

/**
 * Resolves the Provider Portal B2C config from env vars ONLY — independent
 * of `featureFlags.loginProviderPortal`, which only gates whether the
 * provider appears as a *login-page* option. The Create-Auth seamless PP
 * sign-in flow (see auth/ppToken.ts, auth/PpLoginModal.tsx) prompts
 * contextually rather than via the login page, so it must work even in
 * deployments that keep `loginProviderPortal` off.
 */
export function providerPortalB2cConfig(): B2cConfig | null {
  return b2cConfig("provider-portal-b2c", "Atlas account");
}

export function loadEnabledProviders(): AuthProviderConfig[] {
  const wanted = parseEnabledIds();
  const out: AuthProviderConfig[] = [];
  for (const id of wanted) {
    const cfg =
      id === "entra"
        ? entraConfig()
        : id === "pathways-b2c"
          ? b2cConfig("pathways-b2c", "Pathways account")
          : b2cConfig("provider-portal-b2c", "Atlas account");
    if (!cfg) {
      // eslint-disable-next-line no-console
      console.warn(
        `[auth] provider "${id}" is enabled by feature flag but its config env vars are not fully populated — skipping.`,
      );
      continue;
    }
    out.push(cfg);
  }
  return out;
}

/** The single provider the user most recently signed in with. */
const SELECTED_PROVIDER_KEY = "member-360:auth-provider";

export function getSelectedProvider(): AuthProviderId | null {
  try {
    const raw = window.localStorage.getItem(SELECTED_PROVIDER_KEY);
    if (raw === "entra" || raw === "pathways-b2c" || raw === "provider-portal-b2c") return raw;
    return null;
  } catch {
    return null;
  }
}

export function setSelectedProvider(id: AuthProviderId): void {
  try {
    window.localStorage.setItem(SELECTED_PROVIDER_KEY, id);
  } catch {
    /* noop */
  }
}

export function clearSelectedProvider(): void {
  try {
    window.localStorage.removeItem(SELECTED_PROVIDER_KEY);
  } catch {
    /* noop */
  }
}
